import constate from 'constate';
import { useEffect, useMemo } from 'react';
import { JsonParam } from 'use-query-params';
import { useOptionalQueryParamState } from '@/plugin-system/utils/url';

export const INTERNAL_GLOBAL_FILTER = 'Internal filter set by the application';

export interface GlobalFilter {
  label: string;
  matcher: '=' | '!=';
  value: string | string[];
  status?: 'enabled' | 'disabled' | 'override';
  statusReason?: string;
}

export type GlobalFiltersArgs = {
  /**
   * Controlled state for filters. If not provided, the component will manage its own state.
   */
  filters?: GlobalFilter[];

  /**
   * Labels to group by. If not provided, the component will not group by any labels.
   */
  groupByLabels?: string[] | null;

  /**
   * Whether to enable URL params for the global filters. Defaults to false.
   * @default false
   */
  enabledURLParams?: boolean;
};

export type GlobalFilterStore = {
  toggleEnabled: (enabled: boolean) => void;
} & Omit<Required<GlobalFiltersArgs>, 'enabledURLParams' | 'filters'>;

function useGlobalFiltersInternal(args: GlobalFiltersArgs) {
  const argsFilters = useMemo(() => args.filters ?? [], [args.filters]);
  const groupByLabels: string[] = useMemo(() => args.groupByLabels ?? [], [args.groupByLabels]);

  const [filters, setFilters] = useOptionalQueryParamState<GlobalFilter[]>({
    queryParamName: 'filters',
    initialValue: [],
    enableQueryParam: args.enabledURLParams ?? false,
    paramConfig: JsonParam,
  });

  useEffect(() => {
    // If argFilters have been removed, we need to also remove them from local filters state
    const staleFilters: GlobalFilter[] = filters.filter(
      (f) =>
        f.status === 'override' &&
        !argsFilters.some(
          (argFilter) => argFilter.label === f.label && argFilter.value === f.value && argFilter.matcher === f.matcher
        )
    );

    if (staleFilters.length > 0) {
      setFilters(
        filters
          // NB: This is a referential equality check, any re-mapping of `staleFilters` will invalidate this check
          .filter((filter) => !staleFilters.includes(filter))
          .map((filter) => ({
            ...filter,
            // If a filter was overridden by an argFilter that is now stale, re-enable it
            status: staleFilters.some((staleFilter) => staleFilter.label === filter.label) ? 'enabled' : filter.status,
          }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only want to run this effect when the argsFilters change
  }, [argsFilters, setFilters]);

  const mergedFilters = useMemo(() => {
    // Because argFilters are stored in the filters object, we need to remove them first before merging, to ensure we
    // don't have duplicates in the final result
    const dedupedFilters = filters.filter(
      (filter) =>
        !argsFilters.some(
          (argFilter) =>
            argFilter.label === filter.label &&
            argFilter.matcher === filter.matcher &&
            argFilter.value === filter.value &&
            argFilter.status === filter.status
        )
    );
    return (argsFilters ?? []).concat(
      dedupedFilters.map((f) => {
        const overrideFilter = argsFilters.find((af) => f.label === af.label);
        if (overrideFilter !== undefined) {
          return {
            ...f,
            status: 'disabled',
            statusReason: overrideFilter.statusReason ? `Overridden by: ${overrideFilter.statusReason}` : 'Overridden',
          };
        }

        return f;
      })
    );
  }, [argsFilters, filters]);

  return {
    filters: mergedFilters,
    setFilters,
    groupByLabels,
  };
}

const [GlobalFiltersProvider, useGlobalFiltersStore] = constate(useGlobalFiltersInternal);

export { GlobalFiltersProvider };
/**
 * Hook to get the current global filters.
 */
export function useGlobalFilters() {
  const { filters } = useGlobalFiltersStore();
  return filters;
}

export function useGlobalGroupByLabels() {
  const { groupByLabels } = useGlobalFiltersStore();
  return groupByLabels;
}

/**
 * Helper hook to set the global filters.
 */
export function useSetGlobalFilters() {
  const { setFilters } = useGlobalFiltersStore();
  return setFilters;
}
